import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Hero } from "@/components/Hero";
import { TrustCards } from "@/components/TrustCards";
import { WhyChooseUs } from "@/components/WhyChooseUs";
import { PartIdCTA } from "@/components/PartIdCTA";
import { ProcessSteps } from "@/components/ProcessSteps";
import { GalleryGrid } from "@/components/GalleryGrid";
import { FAQAccordion } from "@/components/FAQAccordion";
import { QuoteCTA } from "@/components/QuoteCTA";
import { ContactCards, MapPlaceholder } from "@/components/ContactCards";
import { ProductCard } from "@/components/ProductCard";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/Button";
import { Marquee } from "@/components/Marquee";
import { siteConfig } from "@/data/siteConfig";
import { useSiteContent } from "@/context/ContentContext";

export default function Home() {
  const { contact, categories, faqs } = useSiteContent();
  return (
    <>
      <Hero />
      <TrustCards />

      {/* Product Categories */}
      <section className="bg-white py-20 lg:py-28" data-testid="product-categories">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading kicker="Component Categories" title="The Right Parts for Your Equipment"
              text="Tell us what you need using a part number, machine information or your existing component details." />
            <Button to="/parts" variant="outlineDark" arrow className="shrink-0" data-testid="categories-view-all">Browse Catalogue</Button>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((c, i) => (
              <ProductCard key={i} item={c} index={i} testid={`category-card-${i}`} />
            ))}
          </div>
        </div>
      </section>

      <WhyChooseUs />
      <PartIdCTA />

      {/* Terramech Components Inc. & LAV-TEK — divisions of Ocean Exports */}
      <section className="bg-canvas py-20 lg:py-28" data-testid="ocean-exports">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal className="relative">
            <div className="clip-notch overflow-hidden border border-[#E3E8EF]">
              <img src="https://images.unsplash.com/photo-1689942010216-dc412bb1e7a9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Industrial parts warehouse with racking and forklift" loading="lazy" className="h-[420px] w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -right-5 hidden sm:block bg-ink px-6 py-4">
              <span className="font-display uppercase text-sm tracking-widest text-amber">Terramech Components Inc. & LAV-TEK</span>
              <p className="text-xs text-white/60">Divisions of Ocean Exports</p>
            </div>
          </Reveal>
          <div>
            <SectionHeading kicker="Our Divisions" title="Terramech Components Inc. & LAV-TEK — Divisions of Ocean Exports"
              text="Terramech Components Inc. and LAV-TEK operate as specialised parts divisions under Ocean Exports, extending our aftermarket supply of tractor, implement, backhoe loader and 3-point linkage components." />
            <div className="mt-8"><Button to="/about" variant="primary" arrow data-testid="ocean-about-btn">More About Us</Button></div>
          </div>
        </div>
      </section>

      {/* Supply process */}
      <section className="bg-white py-20 lg:py-28" data-testid="supply-process">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHeading kicker="Supply Process" title="A Clearer Way to Source Replacement Parts" />
          <div className="mt-14"><ProcessSteps /></div>
        </div>
      </section>

      <Marquee items={["Tractor Parts", "Dependable Components", "International Supply", "Precision Sourcing", "B2B Enquiries"]} />

      {/* Gallery preview */}
      <section className="bg-canvas py-20 lg:py-28" data-testid="gallery-preview">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading kicker="Gallery" title="Components. Details. Precision." />
            <Button to="/gallery" variant="outlineDark" arrow className="shrink-0" data-testid="gallery-view-all">View Full Gallery</Button>
          </div>
          <div className="mt-14"><GalleryGrid limit={6} /></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-canvas py-20 lg:py-28" data-testid="home-faq">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <SectionHeading kicker="FAQ" title="Common Questions" align="center" className="text-center" />
          <div className="mt-12"><FAQAccordion faqs={faqs} /></div>
          <div className="mt-8 text-center">
            <Link to="/faq" className="inline-flex items-center gap-2 font-body text-sm font-semibold text-ink hover:text-amber-hover transition-colors" data-testid="home-faq-all">
              View all FAQs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <QuoteCTA />

      {/* Contact */}
      <section className="bg-white py-20 lg:py-28" data-testid="home-contact">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHeading kicker="Contact" title="Reach the Ocean Exports Team" text={siteConfig.company.name} />
          <div className="mt-12"><ContactCards /></div>
          <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1.4fr]">
            <div className="border border-[#E3E8EF] bg-canvas p-8">
              <dl className="space-y-4 text-sm">
                <div><dt className="font-semibold uppercase tracking-wider text-ink text-xs">Address</dt><dd className="mt-1 text-muted">{contact.address}</dd></div>
                <div><dt className="font-semibold uppercase tracking-wider text-ink text-xs">Phone</dt><dd className="mt-1 text-muted">{contact.phoneDisplay}</dd></div>
                <div><dt className="font-semibold uppercase tracking-wider text-ink text-xs">Email</dt><dd className="mt-1 text-muted">{contact.email}</dd></div>
              </dl>
            </div>
            <MapPlaceholder />
          </div>
        </div>
      </section>
    </>
  );
}
